import React, { useEffect, useRef, useState } from "react";
import SEO from "@/components/SEO";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check, MapPin, Music, UtensilsCrossed, Building2 } from "lucide-react";
import FinalCTA from "@/components/FinalCTA";
import BrochureCTA from "@/components/BrochureCTA";
import { CELEBRATION_TYPES } from "@/data/content";
import Img from "@/components/Img";
import { slotKeyFor } from "@/data/imageSlots";

const slugify = (s) => s.replace(/\s+/g, "-");

export default function Celebrations() {
  const [active, setActive] = useState(CELEBRATION_TYPES[0].name);
  const sectionRefs = useRef({});
  const stripRef = useRef(null);
  const location = useLocation();

  // Scroll-spy with IntersectionObserver
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const name = e.target.getAttribute("data-celebration");
            if (name) setActive(name);
          }
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0.01 }
    );
    Object.values(sectionRefs.current).forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  // Scroll active pill horizontally within the strip only (never vertical)
  useEffect(() => {
    const strip = stripRef.current;
    const btn = strip?.querySelector(`[data-pill="${active}"]`);
    if (btn && strip) {
      const targetLeft = btn.offsetLeft - strip.clientWidth / 2 + btn.clientWidth / 2;
      strip.scrollTo({ left: Math.max(0, targetLeft), behavior: "smooth" });
    }
  }, [active]);

  // Handle hash navigation
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [location]);

  return (
    <div data-testid="page-celebrations" className="bg-[#FAF6F1]">
      
      <SEO
        title="Celebration Types — Wedding Rituals, Devotional, Social & Corporate"
        description="Explore wedding rituals, social celebrations, devotional gatherings, and corporate events planned with thoughtful décor, guest hospitality, venue coordination, catering, entertainment, and seamless on-ground execution."
        path="/celebrations"
        keywords="wedding rituals, roka, engagement, mehendi, haldi, sangeet, wedding ceremony, reception, devotional events, mata ki chowki, jagran, corporate launch, anniversary, baby shower"
      />
<section className="pt-28 md:pt-32 pb-12 bg-[#F4DCD8]/35">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="pill">Celebration Types</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[84px] leading-[1.0] mt-6 text-[#3B312D] max-w-5xl">
            From <span className="italic text-[#B65980]">roka</span> to reception, every celebration planned with care.
          </h1>
          <p className="mt-8 max-w-2xl text-[16.5px] text-[#3B312D]/80 leading-relaxed">
            Explore wedding rituals, social celebrations, devotional gatherings, and corporate events planned with thoughtful
            décor, guest hospitality, venue coordination, catering, entertainment, and seamless on-ground execution.
          </p>
        </div>
      </section>

      {/* Sticky celebration nav */}
      <section className="sticky top-20 md:top-[88px] z-30 bg-[#FAF6F1]/95 backdrop-blur-xl border-y border-[rgba(59,49,45,0.08)]">
        <div ref={stripRef} className="max-w-[1400px] mx-auto px-6 md:px-10 py-3 flex gap-2 overflow-x-auto no-scrollbar">
          {CELEBRATION_TYPES.map((c) => (
            <button
              key={c.name}
              data-pill={c.name}
              onClick={() => {
                const el = document.getElementById(`cel-${slugify(c.name)}`);
                el?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              data-testid={`celebration-pill-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-[12px] tracking-[0.14em] uppercase border transition ${
                active === c.name
                  ? "bg-[#3B312D] text-[#FAF6F1] border-[#3B312D]"
                  : "bg-white text-[#3B312D] border-[rgba(59,49,45,0.14)] hover:border-[#B65980] hover:text-[#B65980]"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </section>

      {CELEBRATION_TYPES.map((c, idx) => (
        <section
          key={c.name}
          id={`cel-${slugify(c.name)}`}
          ref={(el) => { if (el) sectionRefs.current[c.name] = el; }}
          data-celebration={c.name}
          className={`py-20 md:py-28 ${idx % 2 === 0 ? "bg-[#FAF6F1]" : "bg-[#F4DCD8]/25"}`}
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className={`lg:col-span-6 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
              <div className="rounded-[22px] overflow-hidden image-zoom h-[400px] md:h-[520px]">
                <Img imageKey={slotKeyFor.celebration(c)} defaultSrc={c.image} defaultAlt={c.name} className="w-full h-full" />
              </div>
            </div>
            <div className="lg:col-span-6">
              <p className="pill-rose">Celebration</p>
              <h2 className="font-display text-4xl md:text-5xl mt-4 text-[#3B312D]">{c.name}</h2>
              <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Detail icon={<Building2 size={14}/>} label="Recommended venue" text={c.venue} />
                <Detail icon={<MapPin size={14}/>} label="Decor direction" text={c.decor} />
                <Detail icon={<UtensilsCrossed size={14}/>} label="Catering" text={c.catering} />
                <Detail icon={<Music size={14}/>} label="Entertainment" text={c.entertainment} />
              </div>
              <div className="mt-7 lux-card p-6">
                <p className="text-[11px] tracking-[0.24em] uppercase text-[#8A9A7B] font-semibold">Planning Checklist</p>
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  {c.checklist.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-[14px] text-[#3B312D]">
                      <Check size={14} className="text-[#B65980] mt-1" /> {it}
                    </li>
                  ))}
                </ul>
              </div>
              <Link to="/contact" className="btn-primary mt-7" data-testid={`celebration-cta-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
                Plan This Celebration <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      ))}

      <BrochureCTA />
      <FinalCTA />
    </div>
  );
}

const Detail = ({ icon, label, text }) => (
  <div>
    <p className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#8A9A7B] font-semibold">{icon}{label}</p>
    <p className="mt-2 text-[14.5px] text-[#3B312D] leading-relaxed">{text}</p>
  </div>
);
